import { Component, Input, OnInit, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Filter } from '../../models/filter-models';
import { ItemDataService } from '../../services/item-data.service';
import { FilterService } from '../../services/filter.service';
import { BaseComponent } from '../../common/base.component';
import { BulkImportGemsDialogComponent } from '../../quick-filter-tab/gem-filters-poe1/bulk-import-gems-dialog/bulk-import-gems-dialog.component';
import { AutocompleteComponent } from '../../common/autocomplete/autocomplete.component';
import { ItemLabelComponent } from '../../common/item-label/item-label.component';
import { BaseType } from '../../models/item-data-models';
import { QuestItemReward, Zone } from '../../models/extra-models';
import { TooltipWrapperComponent } from '../../common/tooltip-wrapper/tooltip-wrapper.component';

interface QuestViewModel {
  key: string;
  name: string;
  act: number;
  sequence: number;
  objective?: string;
  rewards: QuestItemReward[];
  rewardsGems: boolean;
  rewardsSkill: boolean;
  rewardDescription?: string | null;
  rewardExpanded: boolean;
  locations: string[];
  zoneLevels: Map<string, number>;
  hasTrial: boolean;
  matchedGems: string[];
  vendorGems: string[];
  isImportant: boolean;
}

interface ActViewModel {
  act: number;
  quests: QuestViewModel[];
  expanded: boolean;
  manuallyToggled: boolean;
}

interface PartViewModel {
  label: string;
  acts: ActViewModel[];
  expanded: boolean;
}

/** Quests that should not count as the first opportunity for vendor gem availability. */
const BAD_QUESTS = ["A Dirty Job"];

@Component({
  selector: 'app-campaign',
  templateUrl: './campaign.component.html',
  styleUrl: './campaign.component.scss',
  imports: [BulkImportGemsDialogComponent, AutocompleteComponent, ItemLabelComponent, FormsModule, TooltipWrapperComponent]
})
export class CampaignComponent extends BaseComponent implements OnInit {
  @Input({ required: true }) filter!: Filter;

  loading = true;
  acts: ActViewModel[] = [];
  parts: PartViewModel[] = [
    { label: 'Part 1 \u2013 Acts 1\u20135', acts: [], expanded: true },
    { label: 'Part 2 \u2013 Acts 6\u201310', acts: [], expanded: true },
  ];

  bulkImportDialogOpen = false;
  gemTypes: BaseType[] = [];
  showAll = false;

  constructor(private itemDataService: ItemDataService, private filterService: FilterService) {
    super();

    const itemData = this.itemDataService.getItemData();
    const skillGems = itemData.find(i => i.itemType === 'Skill Gems')?.baseTypes
      .filter(b => !this.itemDataService.getTransfiguredGems().includes(b.name)) || [];
    const supportGems = itemData.find(i => i.itemType === 'Support Gems')?.baseTypes || [];
    this.gemTypes = [...skillGems, ...supportGems];

    effect(() => {
      const qr = this.itemDataService.getQuestRewardsData()();
      const gr = this.itemDataService.getGemRewardsData()();
      const vg = this.itemDataService.getVendorGemsData()();
      const zones = this.itemDataService.getZonesData()();

      // this.filter is an @Input and is not yet set during the constructor.
      // Guard so we only build once the filter is available (ngOnInit handles
      // the case where data is already loaded when the component first renders).
      if (qr.length > 0 && gr.length > 0 && vg.length > 0 && zones.length > 0 && this.filter) {
        this.buildViewModel(qr, gr, vg, zones);
        this.loading = false;
      }
    });
  }

  ngOnInit(): void {
    if (!this.filter.completedQuests) {
      this.filter.completedQuests = [];
    }
    // If the data signals already had values when the effect ran in the constructor,
    // this.filter wasn't set yet so the build was skipped. Run it now.
    const qr = this.itemDataService.getQuestRewardsData()();
    const gr = this.itemDataService.getGemRewardsData()();
    const vg = this.itemDataService.getVendorGemsData()();
    const zones = this.itemDataService.getZonesData()();
    if (qr.length > 0 && gr.length > 0 && vg.length > 0 && zones.length > 0 && this.acts.length === 0) {
      this.buildViewModel(qr, gr, vg, zones);
      this.loading = false;
    }
  }

  get searchedGems(): string[] {
    return this.filter.showSearchedGemTypes.filter(g => g !== 'None');
  }

  get hasSearchedGems(): boolean {
    return this.searchedGems.length > 0;
  }

  importGems(gems: BaseType[]): void {
    this.filter.showSearchedGems = true;
    this.filter.showSearchedGemTypes = [
      ...new Set([
        ...this.filter.showSearchedGemTypes.filter(g => g !== 'None'),
        ...gems.map(g => g.name)
      ])
    ];
    if (this.filter.showSearchedGemTypes.length === 0) {
      this.filter.showSearchedGemTypes = ['None'];
    }
    this.rebuildGemMatches();
    this.filterService.updateFilter();
  }

  onSearchedGemsChange(values: string[]): void {
    this.filter.showSearchedGemTypes = values;
    this.rebuildGemMatches();
    this.filterService.updateFilter();
  }

  isQuestCompleted(key: string): boolean {
    return (this.filter.completedQuests || []).includes(key);
  }

  toggleQuest(key: string): void {
    if (!this.filter.completedQuests) {
      this.filter.completedQuests = [];
    }
    if (this.isQuestCompleted(key)) {
      this.filter.completedQuests = this.filter.completedQuests.filter(q => q !== key);
    } else {
      this.filter.completedQuests = [...this.filter.completedQuests, key];
    }
    this.applyAutoCollapse();
    this.filterService.updateFilter();
  }

  resetProgress(): void {
    this.filter.completedQuests = [];
    for (const act of this.acts) {
      act.manuallyToggled = false;
    }
    this.applyAutoCollapse();
    this.filterService.updateFilter();
  }

  toggleAct(actVm: ActViewModel): void {
    actVm.expanded = !actVm.expanded;
    actVm.manuallyToggled = true;
  }

  togglePart(part: PartViewModel): void {
    part.expanded = !part.expanded;
  }

  isQuestVisible(quest: QuestViewModel): boolean {
    if (this.showAll) return true;
    return quest.isImportant || this.isQuestCompleted(quest.key);
  }

  private resolveZoneLevel(locationName: string, act: number, zones: Zone[]): number | null {
    const zone = zones.find(z => z.name === locationName && z.act === act);
    return zone ? zone.lvl : null;
  }

  private buildViewModel(
    questRewards: any[],
    gemRewards: any[],
    vendorGems: any[],
    zones: Zone[]
  ): void {
    // Sort by sequence so first-gem logic works correctly
    const sorted = [...questRewards].sort((a, b) => (a.sequence ?? 999) - (b.sequence ?? 999));

    // Group quests by act+name (merge duplicate entries from different classes)
    const grouped = new Map<string, any[]>();
    for (const quest of sorted) {
      const key = `${quest.act}:${quest.name}`;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(quest);
    }

    const actMap = new Map<number, QuestViewModel[]>();

    // Build initial QuestViewModels without gem dedup (dedup happens after)
    const allQuests: QuestViewModel[] = [];

    for (const [key, entries] of grouped) {
      const first = entries[0];
      const allRewards: QuestItemReward[] = entries.flatMap((e: any) => e.rewards || []);
      const rewardsGems = entries.some((e: any) => e.rewardsGems === true);
      const rewardsSkill = entries.some((e: any) => e.rewardsSkill === true);
      const allLocations: string[] = [...new Set<string>(entries.flatMap((e: any) => e.locations || []))];
      const hasTrial = entries.some((e: any) => e.hasTrial);
      const rewardDescription = first.rewardDescription ?? undefined;

      const zoneLevels = new Map<string, number>();
      for (const loc of allLocations) {
        const lvl = this.resolveZoneLevel(loc, first.act, zones);
        if (lvl !== null) {
          zoneLevels.set(loc, lvl);
        }
      }

      // Raw gem matches (before dedup) - computed here without gem dedup
      const { matchedGems, vendorGemMatches } = this.computeGemMatches(
        first.name, first.act, rewardsGems, gemRewards, vendorGems
      );

      const qvm: QuestViewModel = {
        key,
        name: first.name,
        act: first.act,
        sequence: first.sequence ?? 999,
        objective: first.objective,
        rewards: allRewards,
        rewardsGems,
        rewardsSkill,
        rewardDescription,
        rewardExpanded: false,
        locations: allLocations,
        zoneLevels,
        hasTrial,
        matchedGems,
        vendorGems: vendorGemMatches,
        isImportant: first.isImportant,
      };

      allQuests.push(qvm);

      if (!actMap.has(first.act)) {
        actMap.set(first.act, []);
      }
      actMap.get(first.act)!.push(qvm);
    }

    // Apply first-gem-only dedup across all quests in sequence order
    this.applyFirstGemOnlyDedup(allQuests);

    // Now compute isImportant (needs gem dedup applied first)
    for (const quest of allQuests) {
      quest.isImportant = this.computeIsImportant(quest);
    }

    this.acts = [];
    for (let act = 1; act <= 10; act++) {
      const quests = actMap.get(act) || [];
      quests.sort((a, b) => a.sequence - b.sequence);

      this.acts.push({
        act,
        quests,
        expanded: true,
        manuallyToggled: false,
      });
    }

    this.parts[0].acts = this.acts.filter(a => a.act <= 5);
    this.parts[1].acts = this.acts.filter(a => a.act > 5);

    this.applyAutoCollapse();
  }

  /** Mutates matchedGems/vendorGems on each quest so each gem only appears on its first occurrence.
   *  BAD_QUESTS are skipped for vendor gem attribution: their vendor gems are suppressed and not
   *  marked as seen, so the gem defers to the next eligible quest. */
  private applyFirstGemOnlyDedup(allQuests: QuestViewModel[]): void {
    const sortedQuests = [...allQuests].sort((a, b) => a.sequence - b.sequence);
    const seenGems = new Set<string>();

    for (const quest of sortedQuests) {
      // Direct quest gem rewards: always attributed to this quest (bad or not)
      const newMatchedGems = quest.matchedGems.filter(g => !seenGems.has(g));
      newMatchedGems.forEach(g => seenGems.add(g));
      quest.matchedGems = newMatchedGems;

      // Vendor gems: skip attribution entirely for bad quests so the gem defers
      // to the next non-bad quest where the vendor is still available
      if (BAD_QUESTS.includes(quest.name)) {
        quest.vendorGems = [];
      } else {
        const newVendorGems = quest.vendorGems.filter(g => !seenGems.has(g));
        newVendorGems.forEach(g => seenGems.add(g));
        quest.vendorGems = newVendorGems;
      }
    }
  }

  private computeIsImportant(quest: QuestViewModel): boolean {
    if (BAD_QUESTS.includes(quest.name)) return false;
    if (quest.rewardsSkill) return true;
    if (quest.hasTrial) return true;
    if (quest.isImportant) return true;
    if (quest.matchedGems.length > 0 || quest.vendorGems.length > 0) return true;
    return false;
  }

  private gemsForClass(entries: any[]): Set<string> {
    const selectedClass = this.filter.classPreset;
    const rows: any[] = selectedClass
      ? entries.filter((r: any) => r.class === selectedClass)
      : entries;
    return new Set<string>(rows.flatMap((r: any) => r.gems as string[]));
  }

  private computeGemMatches(
    questName: string,
    act: number,
    rewardsGems: boolean,
    gemRewards: any[],
    vendorGems: any[]
  ): { matchedGems: string[], vendorGemMatches: string[] } {
    if (!this.hasSearchedGems) {
      return { matchedGems: [], vendorGemMatches: [] };
    }

    const searched = new Set(this.searchedGems);
    const matchedGems: string[] = [];
    const vendorGemMatches: string[] = [];

    if (rewardsGems) {
      const gemQuest = gemRewards.find((g: any) => g.name === questName && g.act === act);
      if (gemQuest) {
        const classGems = this.gemsForClass(gemQuest.rewards);
        for (const gem of searched) {
          if (classGems.has(gem)) {
            matchedGems.push(gem);
          }
        }
      }
    }

    // Vendor gems: match by quest name + act (same key as gem-rewards).
    // Exclude gems already rewarded directly by this quest; cross-quest dedup handles the rest.
    const directRewardSet = new Set(matchedGems);
    const questVendorEntry = vendorGems.find((v: any) => v.name === questName && v.act === act);
    if (questVendorEntry) {
      const classVendorGems = this.gemsForClass(questVendorEntry.shop);
      for (const gem of searched) {
        if (classVendorGems.has(gem) && !directRewardSet.has(gem)) {
          vendorGemMatches.push(gem);
        }
      }
    }

    return { matchedGems, vendorGemMatches };
  }

  private rebuildGemMatches(): void {
    const qr = this.itemDataService.getQuestRewardsData()();
    const gr = this.itemDataService.getGemRewardsData()();
    const vg = this.itemDataService.getVendorGemsData()();

    // Recompute raw matches on each quest
    for (const actVm of this.acts) {
      for (const quest of actVm.quests) {
        const { matchedGems, vendorGemMatches } = this.computeGemMatches(
          quest.name, quest.act, quest.rewardsGems, gr, vg
        );
        quest.matchedGems = matchedGems;
        quest.vendorGems = vendorGemMatches;
      }
    }

    // Re-apply first-gem-only dedup
    const allQuests = this.acts.flatMap(a => a.quests);
    this.applyFirstGemOnlyDedup(allQuests);

    // Recompute isImportant
    for (const quest of allQuests) {
      quest.isImportant = this.computeIsImportant(quest);
    }
  }

  private applyAutoCollapse(): void {
    if (!this.acts.length) return;

    let currentAct = 1;
    for (const actVm of this.acts) {
      const hasAnyCompleted = actVm.quests.some(q => this.isQuestCompleted(q.key));
      if (hasAnyCompleted) {
        currentAct = actVm.act;
      }
    }

    const nextAct = Math.min(currentAct + 1, 10);

    for (const actVm of this.acts) {
      if (actVm.manuallyToggled) continue;

      const fullyCompleted = this.isActFullyCompleted(actVm);
      const hasIncompleteSkillReward = actVm.quests.some(q => q.rewardsSkill && !this.isQuestCompleted(q.key));

      if (fullyCompleted) {
        actVm.expanded = false;
      } else if (actVm.act === currentAct || actVm.act === nextAct || (hasIncompleteSkillReward && actVm.act <= currentAct)) {
        actVm.expanded = true;
      } else {
        actVm.expanded = false;
      }
    }
  }

  private isActFullyCompleted(actVm: ActViewModel): boolean {
    const relevantQuests = actVm.quests.filter(q => q.rewardsSkill);
    if (relevantQuests.length === 0) return false;
    return relevantQuests.every(q => this.isQuestCompleted(q.key));
  }
}
